/* SaveToPmosButton — глобален бутон за запазване на snapshot към PMOS Intelligence контекста.
 *
 * Употреба:
 *   <SaveToPmosButton module="health_intelligence" payload={data} bg={bg} />
 *
 * Запазва payload в pmos_context_history → използва се за longitudinal tracking.
 */
import React, { useState } from "react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { formatApiError } from "@/lib/formatApiError";

const MODULE_LABELS = {
  health_intelligence: { bg: "Health Intelligence", en: "Health Intelligence" },
  meal_log:            { bg: "Хранителен дневник",  en: "Meal log" },
  plate_simulator:     { bg: "Симулатор",           en: "Plate simulator" },
  diary:               { bg: "Дневник",             en: "Diary" },
  other:               { bg: "Друго",               en: "Other" },
};

/**
 * @param {object} props
 * @param {"health_intelligence"|"meal_log"|"plate_simulator"|"diary"|"other"} props.module
 * @param {object} props.payload — JSON-serializable snapshot
 * @param {boolean} props.bg
 * @param {string} [props.note]
 * @param {string} [props.size] — sm | default
 * @param {string} [props.variant] — outline | default
 * @param {string} [props.className]
 * @param {string} [props.testId]
 */
export default function SaveToPmosButton(_props) {
  // Hidden per user request — button no longer renders anywhere.
  // To restore: revert this commit (the original implementation is preserved in git history).
  return null;
}

// eslint-disable-next-line no-unused-vars
function _SaveToPmosButtonOriginal({
  module = "other",
  payload,
  bg = true,
  note,
  size = "sm",
  variant = "outline",
  className = "",
  testId,
}) {
  const { API, user } = useAuth();
  const { isDark } = useTheme();
  const [state, setState] = useState("idle"); // idle | loading | saved

  // Only female users + admin see this button (matches PMOS gating)
  const gender = (user?.profile?.personal?.gender || "").toLowerCase();
  const isFemale = ["female", "жена", "f"].includes(gender);
  const isAdmin = user?.role === "admin";
  if (!isFemale && !isAdmin) return null;

  const handleSave = async () => {
    if (!payload || (typeof payload === "object" && Object.keys(payload).length === 0)) {
      toast.info(bg ? "Няма данни за запазване" : "No data to save");
      return;
    }
    setState("loading");
    try {
      await axios.post(`${API}/pmos/quick-save-context`, {
        module,
        payload,
        note,
      });
      setState("saved");
      const label = MODULE_LABELS[module] || MODULE_LABELS.other;
      toast.success(bg
        ? `Записано в PMOS Intelligence от ${label.bg}`
        : `Saved to PMOS Intelligence from ${label.en}`);
      setTimeout(() => setState("idle"), 2500);
    } catch (e) {
      setState("idle");
      toast.error(formatApiError(e, (bg ? "Грешка при запазване" : "Save failed")));
    }
  };

  const isSaved = state === "saved";
  const isLoading = state === "loading";

  return (
    <Button
      type="button"
      size={size}
      variant={variant}
      onClick={handleSave}
      disabled={isLoading || isSaved}
      className={`${className} ${
        isSaved
          ? "border-emerald-500/50 text-emerald-500"
          : isDark
            ? "border-fuchsia-700 text-fuchsia-300 hover:bg-fuchsia-950/30"
            : "border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"
      }`}
      data-testid={testId || `save-to-pmos-${module}`}
    >
      {isLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
        : isSaved ? <Check className="w-3.5 h-3.5 mr-1.5" />
        : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
      {isLoading
        ? (bg ? "Записва..." : "Saving...")
        : isSaved
          ? (bg ? "Включено в PMOS" : "Saved to PMOS")
          : (bg ? "Запази в PMOS Intelligence" : "Save to PMOS Intelligence")}
    </Button>
  );
}
